import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import {
  PendingTimesheet,
  TimesheetEntryDetail,
  TimesheetStats,
  getPendingTimesheets,
  getTimesheetEntries,
  approveTimesheet,
  rejectTimesheet,
  getTimesheetStats,
} from '../../api/client';

interface PendingTimesheetListProps {
  teamId?: string;
  onApprove?: () => void;
}

export default function PendingTimesheetList({
  teamId,
  onApprove,
}: PendingTimesheetListProps) {
  const { user } = useAuth();
  const [timesheets, setTimesheets] = useState<PendingTimesheet[]>([]);
  const [entries, setEntries] = useState<Record<string, TimesheetEntryDetail[]>>({});
  const [stats, setStats] = useState<TimesheetStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [approvalComments, setApprovalComments] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionForm, setShowRejectionForm] = useState(false);

  useEffect(() => {
    fetchData();
  }, [teamId]);

  // Auto-clear messages after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const [timesheetsRes, statsRes] = await Promise.all([
        getPendingTimesheets(teamId),
        getTimesheetStats(teamId),
      ]);

      if (timesheetsRes.success) {
        setTimesheets(timesheetsRes.data || []);
      } else {
        setError(timesheetsRes.error || 'Failed to fetch pending timesheets');
      }

      if (statsRes.success) {
        setStats(statsRes.data || null);
      }
    } catch (err: any) {
      setError(err.message || 'Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTimesheetEntries = async (timesheetId: string) => {
    try {
      if (entries[timesheetId]) {
        return entries[timesheetId];
      }

      const response = await getTimesheetEntries(timesheetId);

      if (response.success) {
        const fetchedEntries = response.data || [];
        setEntries(prev => ({
          ...prev,
          [timesheetId]: fetchedEntries,
        }));
        return fetchedEntries;
      }
      return [];
    } catch (err: any) {
      console.error('Failed to fetch entries:', err);
      return [];
    }
  };

  const handleSelectTimesheet = async (timesheetId: string) => {
    setSelectedId(timesheetId);
    setShowRejectionForm(false);
    setApprovalComments('');
    setRejectionReason('');
    await fetchTimesheetEntries(timesheetId);
  };

  const handleApprove = async () => {
    if (!selectedId || !user) return;

    setActionLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await approveTimesheet(selectedId, user.Email, approvalComments);

      if (response.success) {
        setSuccessMessage('Timesheet approved successfully');
        setSelectedId(null);
        await fetchData();
        if (onApprove) onApprove();
      } else {
        setError(response.error || 'Failed to approve timesheet');
      }
    } catch (err: any) {
      setError(err.message || 'Error approving timesheet');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedId) return;

    setActionLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await rejectTimesheet(selectedId, rejectionReason);

      if (response.success) {
        setSuccessMessage('Timesheet rejected and returned to draft');
        setSelectedId(null);
        setShowRejectionForm(false);
        await fetchData();
        if (onApprove) onApprove();
      } else {
        setError(response.error || 'Failed to reject timesheet');
      }
    } catch (err: any) {
      setError(err.message || 'Error rejecting timesheet');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const calculateTotalHours = (timesheet: PendingTimesheet, timesheetEntries: TimesheetEntryDetail[]) => {
    return timesheet.TotalHours ?? timesheetEntries.reduce((sum, entry) => sum + entry.HoursLogged, 0);
  };

  const groupEntriesByProject = (entries: TimesheetEntryDetail[]) => {
    const grouped: Record<string, { title: string; hours: number }> = {};
    entries.forEach(entry => {
      const key = entry.ProjectId || entry.GLCodeId || 'unassigned';
      const title = entry.ProjectTitle || entry.GLCodeName || 'Unassigned';
      grouped[key] = {
        title,
        hours: (grouped[key]?.hours || 0) + entry.HoursLogged,
      };
    });
    return Object.entries(grouped);
  };

  const selectedTimesheet = timesheets.find(ts => ts.TimesheetId === selectedId);
  const selectedEntries = selectedId ? (entries[selectedId] || []) : [];
  const totalHours = selectedTimesheet ? calculateTotalHours(selectedTimesheet, selectedEntries) : 0;
  const projectBreakdown = selectedEntries.length > 0 ? groupEntriesByProject(selectedEntries) : [];

  if (loading) {
    return (
      <div className="pending-timesheet-list">
        <div className="loading">Loading pending timesheets...</div>
      </div>
    );
  }

  return (
    <div className="pending-timesheet-list">
      {successMessage && (
        <div className="success-message">
          <strong>✓ Success:</strong> {successMessage}
        </div>
      )}

      {error && (
        <div className="error-message">
          <strong>✕ Error:</strong> {error}
        </div>
      )}

      {!selectedId ? (
        <>
          {/* Summary Statistics */}
          {stats && (
            <div className="stats-section">
              <div className="stat-card">
                <div className="stat-label">Pending Approvals</div>
                <div className="stat-value pending">{stats.PendingCount}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Approved This Week</div>
                <div className="stat-value approved">{stats.ApprovedThisWeekCount}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Team Members</div>
                <div className="stat-value">{stats.TeamMemberCount}</div>
              </div>
            </div>
          )}

          {timesheets.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">✓</div>
              <p className="empty-title">All Caught Up!</p>
              <p className="empty-message">There are no pending timesheets to review at this time.</p>
            </div>
          ) : (
            <div className="timesheets-list">
              {timesheets.map(ts => {
                const tsEntries = entries[ts.TimesheetId] || [];
                const tsHours = calculateTotalHours(ts, tsEntries);
                return (
                  <div
                    key={ts.TimesheetId}
                    className="timesheet-card"
                    onClick={() => handleSelectTimesheet(ts.TimesheetId)}
                  >
                    <div className="card-header">
                      <div className="card-title">{ts.UserName}</div>
                      <span className="badge badge-pending">Pending</span>
                    </div>
                    <div className="card-body">
                      <div className="card-row">
                        <span className="label">Week:</span>
                        <span className="value">{formatDate(ts.WeekStartDate)}</span>
                      </div>
                      <div className="card-row">
                        <span className="label">Hours:</span>
                        <span className="value hours-highlight">{tsHours}h</span>
                      </div>
                      <div className="card-row">
                        <span className="label">Submitted:</span>
                        <span className="value">{formatDate(ts.SubmittedDate)}</span>
                      </div>
                    </div>
                    <div className="card-footer">
                      <button className="view-details-btn">View Details →</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <>
          <button className="btn-back" onClick={() => setSelectedId(null)}>
            ← Back to List
          </button>

          {selectedTimesheet && (
            <div className="timesheet-detail">
              <div className="detail-header">
                <div className="header-left">
                  <h2>{selectedTimesheet.UserName}</h2>
                  <p className="detail-email">{selectedTimesheet.UserEmail}</p>
                  <p className="detail-week">
                    Week of {formatDate(selectedTimesheet.WeekStartDate)}
                  </p>
                </div>
                <div className="header-right">
                  <div className="hours-badge">
                    <div className="badge-label">Total Hours</div>
                    <div className="badge-value">{totalHours}h</div>
                  </div>
                </div>
              </div>

              {/* Daily Breakdown */}
              <div className="entries-section">
                <h3>Daily Time Entries</h3>
                {selectedEntries.length === 0 ? (
                  <p className="no-entries">No entries found for this timesheet</p>
                ) : (
                  <div className="entries-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Day</th>
                          <th>Project / GL Code</th>
                          <th>Hours</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedEntries.map(entry => (
                          <tr key={entry.TimesheetEntryId}>
                            <td className="day-col">{entry.DayOfWeek}</td>
                            <td className="project-col">
                              {entry.ProjectTitle || entry.GLCodeName || 'Unassigned'}
                            </td>
                            <td className="hours-cell">{entry.HoursLogged}h</td>
                            <td className="notes-col">{entry.Notes || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Project Breakdown */}
              {projectBreakdown.length > 0 && (
                <div className="project-breakdown-section">
                  <h3>Project Breakdown</h3>
                  <div className="project-breakdown">
                    {projectBreakdown.map(([key, data]) => (
                      <div key={key} className="project-item">
                        <div className="project-name">{data.title}</div>
                        <div className="project-hours">{data.hours}h</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Approval Form */}
              {!showRejectionForm && (
                <div className="approval-form-section">
                  <h3>Approval Notes (Optional)</h3>
                  <textarea
                    className="approval-textarea"
                    placeholder="Add any approval comments or notes..."
                    value={approvalComments}
                    onChange={e => setApprovalComments(e.target.value)}
                    rows={4}
                  />
                </div>
              )}

              {/* Rejection Form */}
              {showRejectionForm && (
                <div className="rejection-form-section">
                  <h3>Rejection Reason</h3>
                  <textarea
                    className="rejection-textarea"
                    placeholder="Please explain why this timesheet is being rejected..."
                    value={rejectionReason}
                    onChange={e => setRejectionReason(e.target.value)}
                    rows={4}
                  />
                </div>
              )}

              {/* Actions */}
              <div className="actions-section">
                {!showRejectionForm ? (
                  <>
                    <button
                      className="btn-approve"
                      onClick={handleApprove}
                      disabled={actionLoading}
                    >
                      {actionLoading ? 'Approving...' : '✓ Approve'}
                    </button>
                    <button
                      className="btn-reject-open"
                      onClick={() => setShowRejectionForm(true)}
                      disabled={actionLoading}
                    >
                      {actionLoading ? 'Processing...' : '✕ Reject'}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="btn-confirm-reject"
                      onClick={handleReject}
                      disabled={actionLoading || !rejectionReason.trim()}
                    >
                      {actionLoading ? 'Rejecting...' : 'Confirm Rejection'}
                    </button>
                    <button
                      className="btn-cancel-reject"
                      onClick={() => {
                        setShowRejectionForm(false);
                        setRejectionReason('');
                      }}
                      disabled={actionLoading}
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <style jsx>{`
        .pending-timesheet-list {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .loading {
          text-align: center;
          padding: 60px 20px;
          color: #666;
          font-size: 16px;
          background: white;
          border-radius: 8px;
          margin-top: 20px;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
          background: white;
          border-radius: 8px;
          margin-top: 20px;
        }

        .empty-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .empty-title {
          margin: 0 0 8px 0;
          font-size: 24px;
          font-weight: 600;
          color: #333;
        }

        .empty-message {
          margin: 0;
          color: #999;
          font-size: 16px;
        }

        .success-message {
          padding: 14px 16px;
          background: #e8f5e9;
          color: #2e7d32;
          border-radius: 6px;
          margin-bottom: 20px;
          border-left: 4px solid #4caf50;
          font-size: 14px;
          animation: slideIn 0.3s ease-in-out;
        }

        .error-message {
          padding: 14px 16px;
          background: #ffebee;
          color: #c62828;
          border-radius: 6px;
          margin-bottom: 20px;
          border-left: 4px solid #f44336;
          font-size: 14px;
          animation: slideIn 0.3s ease-in-out;
        }

        @keyframes slideIn {
          from {
            transform: translateY(-10px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        /* Summary Stats */
        .stats-section {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }

        .stat-card {
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          padding: 20px;
          text-align: center;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        }

        .stat-label {
          font-size: 12px;
          font-weight: 600;
          color: #999;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 8px;
        }

        .stat-value {
          font-size: 32px;
          font-weight: 700;
          color: #333;
        }

        .stat-value.pending {
          color: #ff9800;
        }

        .stat-value.approved {
          color: #4caf50;
        }

        /* Timesheet List */
        .btn-back {
          padding: 10px 16px;
          background: white;
          border: 1px solid #ddd;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          margin-bottom: 20px;
          transition: all 0.2s;
          color: #333;
        }

        .btn-back:hover {
          background: #f5f5f5;
          border-color: #999;
        }

        .timesheets-list {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 16px;
          margin-top: 20px;
        }

        .timesheet-card {
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          overflow: hidden;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        }

        .timesheet-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
          border-color: #2196f3;
          transform: translateY(-2px);
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 16px;
          border-bottom: 1px solid #f0f0f0;
          background: #fafafa;
        }

        .card-title {
          font-size: 16px;
          font-weight: 600;
          color: #333;
        }

        .badge {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        .badge-pending {
          background: #ffe0b2;
          color: #e65100;
        }

        .card-body {
          padding: 16px;
        }

        .card-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          font-size: 13px;
        }

        .card-row:last-child {
          margin-bottom: 0;
        }

        .card-row .label {
          color: #999;
          font-weight: 500;
        }

        .card-row .value {
          color: #333;
          font-weight: 500;
        }

        .hours-highlight {
          color: #ff9800;
          font-weight: 700;
          font-size: 14px;
        }

        .card-footer {
          padding: 0 16px 16px 16px;
        }

        .view-details-btn {
          width: 100%;
          padding: 8px;
          background: #f0f0f0;
          border: none;
          border-radius: 4px;
          font-size: 13px;
          font-weight: 500;
          color: #2196f3;
          cursor: pointer;
          transition: all 0.2s;
        }

        .view-details-btn:hover {
          background: #e3f2fd;
        }

        /* Detail View */
        .timesheet-detail {
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          padding: 24px;
          max-width: 100%;
          overflow-x: auto;
        }

        .detail-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 32px;
          padding-bottom: 20px;
          border-bottom: 2px solid #f0f0f0;
        }

        .header-left h2 {
          margin: 0 0 8px 0;
          font-size: 28px;
          font-weight: 700;
          color: #333;
        }

        .detail-email {
          margin: 0 0 4px 0;
          color: #666;
          font-size: 14px;
        }

        .detail-week {
          margin: 0;
          color: #999;
          font-size: 14px;
        }

        .header-right {
          display: flex;
          align-items: center;
        }

        .hours-badge {
          background: #f0f0f0;
          border-radius: 8px;
          padding: 16px 24px;
          text-align: center;
        }

        .badge-label {
          font-size: 12px;
          font-weight: 600;
          color: #999;
          text-transform: uppercase;
          margin-bottom: 8px;
        }

        .badge-value {
          font-size: 32px;
          font-weight: 700;
          color: #ff9800;
        }

        /* Entries Section */
        .entries-section {
          margin-bottom: 32px;
        }

        .entries-section h3 {
          margin: 0 0 16px 0;
          font-size: 18px;
          font-weight: 600;
          color: #333;
        }

        .no-entries {
          color: #999;
          font-size: 14px;
          margin: 0;
          padding: 16px;
          background: #f9f9f9;
          border-radius: 6px;
          border-left: 4px solid #ddd;
        }

        .entries-table {
          overflow-x: auto;
          border-radius: 6px;
          border: 1px solid #e0e0e0;
        }

        .entries-table table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }

        .entries-table th {
          background: #f5f5f5;
          padding: 12px;
          text-align: left;
          font-weight: 600;
          color: #333;
          border-bottom: 2px solid #e0e0e0;
        }

        .entries-table td {
          padding: 12px;
          border-bottom: 1px solid #f0f0f0;
          color: #666;
        }

        .day-col {
          font-weight: 500;
          width: 80px;
        }

        .project-col {
          font-weight: 500;
        }

        .hours-cell {
          font-weight: 700;
          text-align: right;
          color: #ff9800;
          width: 80px;
        }

        .notes-col {
          color: #999;
          font-size: 13px;
        }

        /* Project Breakdown */
        .project-breakdown-section {
          margin-bottom: 32px;
        }

        .project-breakdown-section h3 {
          margin: 0 0 16px 0;
          font-size: 18px;
          font-weight: 600;
          color: #333;
        }

        .project-breakdown {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 12px;
        }

        .project-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px;
          background: #f9f9f9;
          border-radius: 6px;
          border: 1px solid #f0f0f0;
        }

        .project-name {
          font-weight: 500;
          color: #333;
          flex: 1;
        }

        .project-hours {
          font-weight: 700;
          color: #ff9800;
          margin-left: 12px;
        }

        /* Approval Form */
        .approval-form-section,
        .rejection-form-section {
          margin-bottom: 24px;
          padding: 20px;
          background: #f9f9f9;
          border-radius: 8px;
          border: 1px solid #f0f0f0;
        }

        .approval-form-section h3,
        .rejection-form-section h3 {
          margin: 0 0 12px 0;
          font-size: 16px;
          font-weight: 600;
          color: #333;
        }

        .approval-textarea,
        .rejection-textarea {
          width: 100%;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-family: inherit;
          font-size: 14px;
          color: #333;
          resize: vertical;
        }

        .approval-textarea::placeholder,
        .rejection-textarea::placeholder {
          color: #bbb;
        }

        .approval-textarea:focus,
        .rejection-textarea:focus {
          outline: none;
          border-color: #2196f3;
          box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
        }

        /* Actions Section */
        .actions-section {
          display: flex;
          gap: 12px;
          margin-top: 32px;
          padding-top: 20px;
          border-top: 2px solid #f0f0f0;
        }

        .btn-approve,
        .btn-reject-open,
        .btn-confirm-reject,
        .btn-cancel-reject {
          flex: 1;
          padding: 12px 20px;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 14px;
        }

        .btn-approve {
          background: #4caf50;
          color: white;
        }

        .btn-approve:hover:not(:disabled) {
          background: #45a049;
          box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
        }

        .btn-reject-open {
          background: white;
          color: #f44336;
          border: 1px solid #f44336;
        }

        .btn-reject-open:hover:not(:disabled) {
          background: #ffebee;
        }

        .btn-confirm-reject {
          background: #f44336;
          color: white;
        }

        .btn-confirm-reject:hover:not(:disabled) {
          background: #da190b;
          box-shadow: 0 2px 8px rgba(244, 67, 54, 0.3);
        }

        .btn-confirm-reject:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-cancel-reject {
          background: white;
          color: #999;
          border: 1px solid #ddd;
        }

        .btn-cancel-reject:hover:not(:disabled) {
          background: #f5f5f5;
          border-color: #999;
        }

        .btn-approve:disabled,
        .btn-reject-open:disabled,
        .btn-cancel-reject:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .detail-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .header-right {
            width: 100%;
            margin-top: 16px;
          }

          .actions-section {
            flex-direction: column;
          }

          .timesheets-list {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}
